/**
 * Imágenes del landing que quedaron sin uso después de guardar.
 *
 * Al reemplazar o quitar la imagen de un evento o la foto de un docente, el
 * archivo anterior quedaba en Storage para siempre. Se borra recién DESPUÉS
 * de guardar con éxito: si se borrara al hacer clic en «Quitar» y el director
 * no guarda, el landing quedaría apuntando a un archivo que ya no existe.
 *
 * Solo se consideran archivos que este editor sube: en
 * `tenants/{tenantId}/assets/` con prefijo `featured-event-` o `faculty-`.
 * Nunca el logo, el fondo de la portada ni archivos de otro seminario.
 *
 * Límite conocido: una imagen subida y descartada sin guardar sigue quedando
 * huérfana (nunca llegó a estar guardada). Es poco volumen.
 */
import type { LandingConfig } from '@dosfilos/domain';

const OWNED_PREFIXES = ['featured-event-', 'faculty-'];

/** La ruta de Storage de una URL de descarga de Firebase (también del emulador). */
export function storagePathOf(url: string): string | null {
  try {
    const u = new URL(url);
    const i = u.pathname.indexOf('/o/');
    if (i < 0) return null;
    return decodeURIComponent(u.pathname.slice(i + 3));
  } catch {
    return null;
  }
}

function isOwned(url: string, tenantId: string): boolean {
  const path = storagePathOf(url);
  if (!path) return false;
  const dir = `tenants/${tenantId}/assets/`;
  if (!path.startsWith(dir)) return false;
  const file = path.slice(dir.length);
  return !file.includes('/') && OWNED_PREFIXES.some((p) => file.startsWith(p));
}

function landingImages(l: Partial<LandingConfig> | null | undefined): Set<string> {
  const urls = new Set<string>();
  for (const e of l?.featuredEvents ?? []) if (e.imageUrl) urls.add(e.imageUrl);
  for (const m of l?.faculty ?? []) if (m.photoUrl) urls.add(m.photoUrl);
  return urls;
}

/** Las URLs que estaban en lo guardado antes y ya no están en lo guardado ahora. */
export function orphanedLandingImages(
  before: Partial<LandingConfig> | null | undefined,
  after: Partial<LandingConfig> | null | undefined,
  tenantId: string,
): string[] {
  const now = landingImages(after);
  return [...landingImages(before)].filter((url) => !now.has(url) && isOwned(url, tenantId));
}
